import React, { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import {
  generateFinanceTaxStatements,
  GenerateFinanceTaxStatementsDonorType,
  getTaxStatementDownloadUrl,
  useFinanceTaxStatements,
} from '../hooks/useFinanceTaxStatements';
import { supabase } from '../services/supabase';
import { Table, TableWrap } from '../styles/marketingSharedStyles';

type DonorContributionRow = {
  id: string;
  amount: number;
  currency: string | null;
  category: string | null;
  payment_method: string | null;
  status: string | null;
  created_at: string;
};

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.65rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.35rem;
  color: var(--onSurface);
`;

const HeaderSub = styled.p`
  margin: 0.25rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
`;

const BackLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const Card = styled.section`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1rem 1.1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.9rem;
`;

const MetricLabel = styled.p`
  margin: 0;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
`;

const MetricValue = styled.h2`
  margin: 0.35rem 0 0;
  font-size: 1.5rem;
  color: var(--onSurface);
`;

const MetricSub = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
  margin-bottom: 0.8rem;
  flex-wrap: wrap;
`;

const Select = styled.select`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.85rem;
`;

const HelperText = styled.p`
  margin: 0.45rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const InlineError = styled.p`
  margin: 0.45rem 0 0;
  color: var(--error);
  font-size: 0.82rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.65rem;
  margin-top: 0.75rem;
`;

const Input = styled.input`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.85rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 0.75rem;
`;

const ActionButton = styled.button<{ $primary?: boolean; $destructive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid
    ${({ $destructive, $primary }) =>
      $destructive ? 'var(--error)' : $primary ? 'var(--primary)' : 'var(--outline)'};
  background: ${({ $destructive, $primary }) => ($destructive ? 'var(--error)' : $primary ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $destructive, $primary }) => ($destructive || $primary ? 'var(--onPrimary)' : 'var(--onSurface)')};
  border-radius: 8px;
  padding: 0.5rem 0.8rem;
  font-size: 0.83rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatementList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-top: 0.75rem;
`;

const StatementRow = styled.div`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.65rem;
`;

const StatementMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

const StatementTitle = styled.p`
  margin: 0;
  color: var(--onSurface);
  font-size: 0.86rem;
  font-weight: 600;
`;

const StatementSub = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.78rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

function formatCurrency(value: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

type ManualContributionPayload = {
  amount: number;
  currency: string;
  category: string;
  payment_method: string;
  status: string;
  created_at: string;
  donor_name?: string;
  donor_email?: string;
  user_id?: string;
  notes?: string;
};

export default function FinanceDonorPage() {
  const queryClient = useQueryClient();
  const { donorType, donorValue } = useParams<{ donorType?: string; donorValue?: string }>();
  const location = useLocation();
  const donorLabelFromState = (location.state as { donorLabel?: string } | null)?.donorLabel ?? null;
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [manualStatus, setManualStatus] = useState('completed');
  const [manualMethod, setManualMethod] = useState('manual');
  const [manualNotes, setManualNotes] = useState('');
  const [manualSaving, setManualSaving] = useState(false);
  const [manualFeedback, setManualFeedback] = useState<string | null>(null);
  const [manualError, setManualError] = useState<string | null>(null);
  const [deleteFeedback, setDeleteFeedback] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingContributionId, setDeletingContributionId] = useState<string | null>(null);
  const [generateTaxYear, setGenerateTaxYear] = useState(String(new Date().getFullYear()));
  const [generateTaxMode, setGenerateTaxMode] = useState<'skip_existing' | 'replace_existing'>('skip_existing');
  const [generatingStatement, setGeneratingStatement] = useState(false);
  const [statementFeedback, setStatementFeedback] = useState<string | null>(null);
  const [statementError, setStatementError] = useState<string | null>(null);
  const [downloadStatementId, setDownloadStatementId] = useState<string | null>(null);
  const decodedValue = decodeURIComponent(donorValue ?? '');

  const donorContributions = useQuery({
    queryKey: ['finance', 'donor-page', donorType ?? '', decodedValue, fromDate, toDate],
    enabled: Boolean(donorType && decodedValue),
    queryFn: async (): Promise<DonorContributionRow[]> => {
      if (!donorType || !decodedValue) return [];
      let query = supabase
        .from('donations')
        .select('id, amount, currency, category, payment_method, status, created_at')
        .order('created_at', { ascending: false });

      if (donorType === 'user') {
        query = query.eq('user_id', decodedValue);
      } else if (donorType === 'email') {
        query = query.eq('donor_email', decodedValue);
      } else {
        query = query.eq('donor_name', decodedValue);
      }
      if (fromDate) {
        query = query.gte('created_at', `${fromDate}T00:00:00`);
      }
      if (toDate) {
        query = query.lte('created_at', `${toDate}T23:59:59`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as DonorContributionRow[];
    },
  });

  const statementsQuery = useFinanceTaxStatements(Boolean(donorType && decodedValue));

  const categories = useMemo(
    () =>
      Array.from(
        new Set((donorContributions.data ?? []).map((row) => (row.category || 'General').trim()))
      ).sort((a, b) => a.localeCompare(b)),
    [donorContributions.data]
  );

  const filteredRows = useMemo(() => {
    const rows = donorContributions.data ?? [];
    if (categoryFilter === 'all') return rows;
    return rows.filter((row) => (row.category || 'General').trim() === categoryFilter);
  }, [categoryFilter, donorContributions.data]);

  const totals = useMemo(() => {
    const rows = filteredRows;
    const isRemovedStatus = (status: string | null) => {
      const normalized = (status || '').trim().toLowerCase();
      return normalized === 'deleted' || normalized === 'removed';
    };
    const totalAll = rows
      .filter((row) => !isRemovedStatus(row.status))
      .reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const totalNonPending = rows
      .filter((row) => {
        const normalized = (row.status || '').trim().toLowerCase();
        return normalized !== 'pending' && normalized !== 'deleted' && normalized !== 'removed';
      })
      .reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const currency = rows[0]?.currency || 'USD';
    return { totalAll, totalNonPending, currency, count: rows.length };
  }, [filteredRows]);

  const donorTitle = donorLabelFromState || decodedValue || 'Donor';
  const donorTypeSafe = donorType ?? '';
  const donorTypeForGenerate = useMemo<GenerateFinanceTaxStatementsDonorType | null>(() => {
    if (donorTypeSafe === 'user') return 'user';
    if (donorTypeSafe === 'email') return 'email';
    if (donorTypeSafe === 'name') return 'name';
    return null;
  }, [donorTypeSafe]);

  const donorStatements = useMemo(() => {
    const list = statementsQuery.data ?? [];
    const normalizedValue = decodedValue.trim().toLowerCase();
    const selectedYear = Number(generateTaxYear);
    if (!normalizedValue || !donorTypeSafe) return [];
    if (!Number.isInteger(selectedYear) || selectedYear < 1900 || selectedYear > 9999) return [];

    return list.filter((statement) => {
      if (statement.tax_year !== selectedYear) return false;
      if (donorTypeSafe === 'user') return statement.user_id === decodedValue;
      if (donorTypeSafe === 'email') return (statement.donor_email || '').trim().toLowerCase() === normalizedValue;
      return (statement.donor_name || '').trim().toLowerCase() === normalizedValue;
    });
  }, [decodedValue, donorTypeSafe, generateTaxYear, statementsQuery.data]);

  const withDonorIdentity = (row: ManualContributionPayload) => {
    const next = { ...row };
    if (donorTypeSafe === 'user') next.user_id = decodedValue;
    if (donorTypeSafe === 'email') next.donor_email = decodedValue;
    if (donorTypeSafe === 'name') next.donor_name = decodedValue;
    return next;
  };

  const handleAddInHouseContribution = async () => {
    const amount = Number(manualAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setManualError('Enter a valid amount greater than 0.');
      setManualFeedback(null);
      return;
    }
    if (!donorTypeSafe || !decodedValue) {
      setManualError('Missing donor context for manual contribution.');
      setManualFeedback(null);
      return;
    }

    setManualSaving(true);
    setManualError(null);
    setManualFeedback(null);
    try {
      const createdAt = manualDate ? `${manualDate}T12:00:00.000Z` : new Date().toISOString();
      const row: ManualContributionPayload = {
        amount,
        currency: 'USD',
        category: 'in-house',
        payment_method: manualMethod,
        status: manualStatus,
        created_at: createdAt,
      };

      if (manualNotes.trim()) row.notes = manualNotes.trim();
      const rowWithDonor = withDonorIdentity(row);

      const { data, error } = await supabase.functions.invoke('finance-import-contributions', {
        body: { rows: [rowWithDonor] },
      });
      if (error) throw error;
      const payload = (data ?? {}) as { created?: number; failed?: number; errors?: Array<{ message?: string }> };
      if ((payload.created ?? 0) < 1) {
        const message = payload.errors?.[0]?.message || 'Could not save contribution.';
        throw new Error(message);
      }

      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations-summary'] });
      await queryClient.invalidateQueries({ queryKey: ['finance', 'donor-page'] });
      setManualAmount('');
      setManualDate('');
      setManualNotes('');
      setManualMethod('manual');
      setManualStatus('completed');
      setManualFeedback('In-house contribution added.');
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Failed to add in-house contribution.');
    } finally {
      setManualSaving(false);
    }
  };

  const handleGenerateDonorStatement = async () => {
    const year = Number(generateTaxYear);
    if (!Number.isInteger(year) || year < 1900 || year > 9999) {
      setStatementError('Enter a valid 4-digit year.');
      setStatementFeedback(null);
      return;
    }
    if (!donorTypeForGenerate || !decodedValue) {
      setStatementError('Missing donor context for statement generation.');
      setStatementFeedback(null);
      return;
    }

    setGeneratingStatement(true);
    setStatementError(null);
    setStatementFeedback(null);
    try {
      const result = await generateFinanceTaxStatements(year, generateTaxMode, {
        donorType: donorTypeForGenerate,
        donorValue: decodedValue,
      });
      await queryClient.invalidateQueries({ queryKey: ['finance', 'tax-statements'] });
      setStatementFeedback(
        `Generated for ${result.taxYear}. Created ${result.created}, replaced ${result.replaced}, skipped ${result.skipped}, failed ${result.failed}.`
      );
      if (result.errors.length > 0) {
        setStatementError(result.errors.slice(0, 2).join(' '));
      }
    } catch (err) {
      setStatementError(err instanceof Error ? err.message : 'Failed to generate donor statement.');
    } finally {
      setGeneratingStatement(false);
    }
  };

  const handleDownloadStatement = async (statementId: string) => {
    setDownloadStatementId(statementId);
    setStatementError(null);
    try {
      const { url, fileName } = await getTaxStatementDownloadUrl(statementId);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_blank';
      link.rel = 'noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setStatementError(err instanceof Error ? err.message : 'Failed to download statement.');
    } finally {
      setDownloadStatementId(null);
    }
  };

  const handleDeleteContribution = async (contributionId: string) => {
    if (!contributionId) return;
    const confirmed = window.confirm('Mark this contribution as deleted? It will remain in records but not count toward totals.');
    if (!confirmed) return;

    setDeletingContributionId(contributionId);
    setDeleteError(null);
    setDeleteFeedback(null);
    try {
      const { data, error } = await supabase.functions.invoke('finance-delete-contribution', {
        body: {
          contributionId,
          donorType: donorTypeSafe,
          donorValue: decodedValue,
        },
      });
      if (error) throw error;
      const payload = (data ?? {}) as { deleted?: boolean; error?: string };
      if (payload.error) throw new Error(payload.error);
      if (!payload.deleted) throw new Error('Contribution was not deleted.');

      await queryClient.invalidateQueries({ queryKey: ['finance', 'donor-page'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'donations-summary'] });
      await queryClient.invalidateQueries({ queryKey: ['finance', 'this-month-total-non-pending'] });
      setDeleteFeedback('Contribution marked as deleted.');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete contribution.');
    } finally {
      setDeletingContributionId(null);
    }
  };

  return (
    <Page>
      <Header>
        <div>
          <HeaderTitle>Donor Contributions</HeaderTitle>
          <HeaderSub>{donorTitle}</HeaderSub>
        </div>
        <BackLink to="/finance">Back to Finance</BackLink>
      </Header>

      <Grid>
        <Card>
          <MetricLabel>Total Contributions</MetricLabel>
          <MetricValue>{formatCurrency(totals.totalAll, totals.currency)}</MetricValue>
          <MetricSub>Excludes deleted/removed</MetricSub>
        </Card>
        <Card>
          <MetricLabel>Cleared Contributions</MetricLabel>
          <MetricValue>{formatCurrency(totals.totalNonPending, totals.currency)}</MetricValue>
          <MetricSub>Excludes pending/deleted/removed</MetricSub>
        </Card>
        <Card>
          <MetricLabel>Transactions</MetricLabel>
          <MetricValue>{totals.count}</MetricValue>
          <MetricSub>After active category filter</MetricSub>
        </Card>
      </Grid>

      <Card>
        <h2 style={{ margin: 0, fontSize: '1rem', color: 'var(--onSurface)' }}>Tax Statements</h2>
        <HelperText>Generate tax statements for this donor only.</HelperText>
        <FormGrid>
          <Input
            type="number"
            min="1900"
            max="9999"
            step="1"
            placeholder="Tax year"
            value={generateTaxYear}
            onChange={(event) => setGenerateTaxYear(event.target.value)}
          />
          <Select value={generateTaxMode} onChange={(event) => setGenerateTaxMode(event.target.value as 'skip_existing' | 'replace_existing')}>
            <option value="skip_existing">Skip Existing</option>
            <option value="replace_existing">Replace Existing</option>
          </Select>
        </FormGrid>
        <FormRow>
          <ActionButton
            type="button"
            $primary
            disabled={generatingStatement || !donorTypeForGenerate || !decodedValue}
            onClick={handleGenerateDonorStatement}
          >
            {generatingStatement ? 'Generating...' : 'Generate for this donor'}
          </ActionButton>
        </FormRow>
        {statementFeedback ? <HelperText>{statementFeedback}</HelperText> : null}
        {statementError ? <InlineError>{statementError}</InlineError> : null}
        <HelperText>Showing statements for tax year {generateTaxYear || 'selected year'}.</HelperText>

        {statementsQuery.isLoading ? <HelperText>Loading existing statements...</HelperText> : null}
        {statementsQuery.error ? <InlineError>Unable to load existing statements.</InlineError> : null}
        {!statementsQuery.isLoading && !statementsQuery.error ? (
          <StatementList>
            {donorStatements.map((statement) => (
              <StatementRow key={statement.id}>
                <StatementMeta>
                  <StatementTitle>{statement.display_name || statement.file_name}</StatementTitle>
                  <StatementSub>
                    {statement.tax_year} | Generated {formatDate(statement.generated_at || statement.created_at)}
                  </StatementSub>
                </StatementMeta>
                <ActionButton
                  type="button"
                  onClick={() => handleDownloadStatement(statement.id)}
                  disabled={downloadStatementId === statement.id}
                >
                  {downloadStatementId === statement.id ? 'Preparing...' : 'Download'}
                </ActionButton>
              </StatementRow>
            ))}
            {donorStatements.length === 0 ? <HelperText>No generated statements found for this donor.</HelperText> : null}
          </StatementList>
        ) : null}
      </Card>

      <Card>
        <h2 style={{ margin: 0, fontSize: '1rem', color: 'var(--onSurface)' }}>Add In-House Contribution</h2>
        <HelperText>Creates a manual contribution for this donor with category set to in-house.</HelperText>
        <FormGrid>
          <Input
            type="number"
            min="0.01"
            step="0.01"
            placeholder="Amount (USD)"
            value={manualAmount}
            onChange={(event) => setManualAmount(event.target.value)}
          />
          <Input type="date" value={manualDate} onChange={(event) => setManualDate(event.target.value)} />
          <Select value={manualStatus} onChange={(event) => setManualStatus(event.target.value)}>
            <option value="completed">completed</option>
            <option value="pending">pending</option>
            <option value="failed">failed</option>
            <option value="refunded">refunded</option>
          </Select>
          <Select value={manualMethod} onChange={(event) => setManualMethod(event.target.value)}>
            <option value="manual">manual</option>
            <option value="cash">cash</option>
            <option value="bank_transfer">bank_transfer</option>
            <option value="cheque">cheque</option>
          </Select>
          <Input
            type="text"
            placeholder="Optional notes"
            value={manualNotes}
            onChange={(event) => setManualNotes(event.target.value)}
          />
        </FormGrid>
        <FormRow>
          <ActionButton type="button" $primary disabled={manualSaving} onClick={handleAddInHouseContribution}>
            {manualSaving ? 'Saving...' : 'Add In-House Contribution'}
          </ActionButton>
        </FormRow>
        {manualFeedback ? <HelperText>{manualFeedback}</HelperText> : null}
        {manualError ? <InlineError>{manualError}</InlineError> : null}
      </Card>

      <Card>
        <FilterRow>
          <h2 style={{ margin: 0, fontSize: '1rem', color: 'var(--onSurface)' }}>Contribution History</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              style={{
                border: '1px solid var(--outline)',
                borderRadius: '8px',
                padding: '0.45rem 0.6rem',
                background: 'var(--surface)',
                color: 'var(--onSurface)',
                fontSize: '0.85rem',
              }}
            />
            <input
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              style={{
                border: '1px solid var(--outline)',
                borderRadius: '8px',
                padding: '0.45rem 0.6rem',
                background: 'var(--surface)',
                color: 'var(--onSurface)',
                fontSize: '0.85rem',
              }}
            />
            <Select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
              <option value="all">All categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Select>
          </div>
        </FilterRow>
        {donorContributions.isLoading ? <HelperText>Loading contributions...</HelperText> : null}
        {donorContributions.error ? <InlineError>Unable to load donor contributions.</InlineError> : null}
        {deleteFeedback ? <HelperText>{deleteFeedback}</HelperText> : null}
        {deleteError ? <InlineError>{deleteError}</InlineError> : null}
        {!donorContributions.isLoading && !donorContributions.error ? (
          <TableWrap>
            <Table $mobileCards>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Date">{formatDate(row.created_at)}</td>
                    <td data-label="Category">{row.category || 'General'}</td>
                    <td data-label="Amount">{formatCurrency(Number(row.amount || 0), row.currency || 'USD')}</td>
                    <td data-label="Status">{row.status || 'unknown'}</td>
                    <td data-label="Method">{row.payment_method || 'manual'}</td>
                    <td data-label="Actions">
                      <ActionButton
                        type="button"
                        $destructive
                        onClick={() => handleDeleteContribution(row.id)}
                        disabled={deletingContributionId === row.id}
                      >
                        {deletingContributionId === row.id ? 'Updating...' : 'Mark deleted'}
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        ) : null}
        {!donorContributions.isLoading && !donorContributions.error && filteredRows.length === 0 ? (
          <HelperText>No contributions found for the selected filter.</HelperText>
        ) : null}
      </Card>
    </Page>
  );
}
